import {
  COMMUNITY_COMPASS_REPORT_SAMPLE,
  COMPARATIVE_LOCATION_ANALYSIS_REPORT_SAMPLE,
  FEASIBILITY_STUDY_REPORT_SAMPLE,
  LOCATION_INTELLIGENCE_REPORT_SAMPLE,
  OPEN_HOUSE_FLYER_REPORT_SAMPLE,
  ReportTypes,
} from '../../../../user/user/user.model';
import {
  FEASIBILITY_STUDY_NAME,
  FEASIBILITY_STUDY_PRICE_LABEL,
} from '../../../../sales-forecast/feasibility-study.constants';
import { CheckoutMode, LocationMode } from '../../order-report.model';

/**
 * Who a report is for, which is how the order flow groups the catalogue -
 * people arrive knowing what they are, not which of our reports they need.
 *
 * Realtors get their own group rather than being folded into `living`: they
 * buy for clients, not for themselves, and they're the audience most likely to
 * come back. A report belongs to as many groups as genuinely serve it (the
 * Community compass is bought by homebuyers *and* realtors), so it renders in
 * each of them - one card, listed wherever it's the right answer.
 */
export type ReportAudience = 'living' | 'realtor' | 'business';

/**
 * Rendered in this order, and the first one opens by default - business
 * first, because it's the decision with the most riding on it and the
 * reports that pay for the rest.
 */
export const AUDIENCE_GROUPS: {
  id: ReportAudience;
  title: string;
  note: string;
}[] = [
  {
    id: 'business',
    title: 'For your business',
    note: 'Backing a site decision, a lender conversation or an investment.',
  },
  {
    id: 'realtor',
    title: 'For realtors',
    note: 'Something to send a client, or hand out at a showing.',
  },
  {
    id: 'living',
    title: 'For where you live',
    note: 'Sizing up a neighborhood before you buy, rent or move.',
  },
];

export type ReportData = {
  id: ReportTypes;
  title: string;
  /** Every group this report belongs in. Order follows `AUDIENCE_GROUPS`. */
  audiences: ReportAudience[];
  audience: string;
  // Short who-it's-for label, rendered as a chip on /pricing (same
  // vocabulary as `PLAN_CATEGORY_LABEL` for subscriptions: Homebuyer,
  // Realtor, Business…). `audience` stays the longer parenthetical the
  // order flow's type-card prints under the title.
  category: string;
  sample: string;
  description: string;
  features: string[];
  footer: string;
  // How this report is bought. The single switch the review step branches on -
  // see `CheckoutMode`. Replaced the old `forInnerUse` flag plus the
  // `readyToOrdersReports` array that lived in the component: those encoded the
  // same three cases in two places that could disagree.
  checkout: CheckoutMode;
  // What the location step asks for: an area around a pin, or just the pin.
  // See `LocationMode` - only the two reports that summarise a neighbourhood
  // use a radius.
  location: LocationMode;
  // How long from order to delivery, in the visitor's words. Shown on the
  // review step so nobody pays expecting an instant PDF from an analyst.
  turnaround: string;
  priceInfo: {
    /** Marketing copy, not a charge - see `ReportsService.priceUsdFor`. */
    price: string;
  };
};

/**
 * Report id → display name, for anywhere that has an id and no card - today
 * the My reports table. Lives here beside the catalogue rather than on the
 * order flow's component so that page doesn't import a screen it never shows.
 */
export function reportTitle(id: ReportTypes | string): string {
  return findReport(id as ReportTypes)?.title ?? String(id);
}

/** The reports in one audience group, in catalogue order. */
export function reportsFor(audience: ReportAudience): ReportData[] {
  return reportData.filter((r) => r.audiences.includes(audience));
}

/**
 * The one report we put forward per group, badged "Recommended". Exactly one
 * per group and never zero: a group where everything looks equally good is a
 * group that makes the visitor do our job.
 *
 * Business gets the feasibility study because it's the deliverable that
 * actually settles a site decision; both people-facing groups get the
 * community compass, the only report they can buy and read in one sitting.
 */
export const RECOMMENDED_BY_AUDIENCE: Record<ReportAudience, ReportTypes> = {
  business: ReportTypes.FEASIBILITY_STUDY,
  realtor: ReportTypes.COMMUNITY_COMPASS,
  living: ReportTypes.COMMUNITY_COMPASS,
};

/**
 * Which report to badge, honouring an inbound `?best=` so a campaign can point
 * at whatever it advertised - same contract as /pricing's `?best=` for plans.
 *
 * Accepts the report id (`?best=FEASIBILITY_STUDY`) or its category chip
 * (`?best=Realtor`), so an ad link doesn't have to know the enum. Ignored when
 * it names a report that isn't in this group, so a link aimed at one audience
 * can't badge something nonsensical in another.
 */
export function resolveRecommended(
  audience: ReportAudience,
  bestParam?: string | null
): ReportTypes {
  const wanted = bestParam?.trim().toUpperCase();
  if (!wanted) return RECOMMENDED_BY_AUDIENCE[audience];

  const match = reportsFor(audience).find(
    (report) =>
      report.id.toUpperCase() === wanted ||
      report.category.toUpperCase() === wanted
  );

  return match?.id ?? RECOMMENDED_BY_AUDIENCE[audience];
}

/**
 * Puts the recommended report first, otherwise preserving catalogue order. The
 * badge alone isn't enough - in a three-card row the highlighted one is easy to
 * skip past, and on mobile the grid is a single column, so a recommendation
 * further down would sit below the fold. Same reasoning as /pricing's
 * `plansHighlightedFirst`.
 */
export function recommendedFirst(
  reports: ReportData[],
  recommended: ReportTypes
): ReportData[] {
  const pick = reports.find((report) => report.id === recommended);
  if (!pick) return [...reports];

  return [pick, ...reports.filter((report) => report.id !== recommended)];
}

/** Reports we generate on the spot, in catalogue order. */
export function instantReports(): ReportData[] {
  return reportData.filter((r) => r.checkout !== 'analyst');
}

/** Reports a person prepares, in catalogue order. */
export function analystReports(): ReportData[] {
  return reportData.filter((r) => r.checkout === 'analyst');
}

export function findReport(id: ReportTypes): ReportData | undefined {
  return reportData.find((r) => r.id === id);
}

export const reportData: ReportData[] = [
  {
    id: ReportTypes.COMMUNITY_COMPASS,
    title: 'Community compass',
    // Bought by both, so it is listed under both.
    audiences: ['living', 'realtor'],
    audience: '(for homebuyers / realtors)',
    category: 'Homebuyer / Realtor',
    sample: COMMUNITY_COMPASS_REPORT_SAMPLE,
    description: 'Get clear neighborhood insights in a detailed report',
    features: [
      'Neighborhood summary',
      'Real estate',
      'Demographic',
      'Health',
      'Schools',
      'Crime',
      'Entertainment & Convenience',
      'Commute & Mobility',
    ],
    // The only report with an interactive web version - everything else is a
    // PDF a person prepares.
    footer: 'Interactive report + PDF file',
    checkout: 'instant',
    location: 'radius',
    turnaround: 'Ready in about 3 minutes',
    priceInfo: {
      price: '$9.95',
    },
  },
  {
    id: ReportTypes.OPEN_HOUSE_FLYER,
    title: 'Open house flyer',
    audiences: ['realtor'],
    sample: OPEN_HOUSE_FLYER_REPORT_SAMPLE,
    audience: '(for realtors)',
    category: 'Realtor',
    description: 'Neighborhood stats presented in a concise two-page flyer',
    features: [
      'Location overview',
      'Demographics',
      'Amenities',
      'Schools',
      'Real estate',
    ],
    footer: 'PDF file',
    checkout: 'analyst',
    // One listing. A radius around it would be a number no flyer ever prints.
    location: 'pin',
    turnaround: 'Our analysts reply within one business day',
    priceInfo: {
      price: '$4.99',
    },
  },
  // Traffic measured at one street address, for retail site selection. The
  // `id` stays LOCATION_INTELLIGENCE - that string is the backend's
  // `ReportType` and renaming it would orphan every existing order.
  //
  // Business only: it answers "how much traffic passes this door", which is a
  // site-selection question, not something a realtor takes to a listing.
  {
    id: ReportTypes.LOCATION_INTELLIGENCE,
    sample: LOCATION_INTELLIGENCE_REPORT_SAMPLE,
    title: 'Business Site Traffic',
    audiences: ['business'],
    audience: '(for retailers & store operators)',
    category: 'Business',
    description:
      'Decision support for retail site selection, from traffic measured at ' +
      'your exact address',
    features: [
      'True values of average daily traffic',
      'Intra-day distribution of traffic flows',
      'Daily, monthly and yearly averages for traffic volumes',
      'Traffic flow speed and driver behaviour data',
      'Featured demographic information',
      'Congestion and rush-hour analysis',
      // The three differentiators below are the reason for the price: this is
      // observed traffic at the address, not a model draped over a ZIP code.
      'Measured at the exact street address - not a ZIP code, polygon, ' +
        'miles-long TMC segment or "nearby street"',
      'Continuous 24/7/365 observation across the whole period of interest - ' +
        'not an "average week" or a few days of tube-counter study',
      'Current to within a week, not a "last available" reading that may be ' +
        'years old',
    ],
    footer: 'PDF report',
    checkout: 'analyst',
    // Traffic is observed at the door, so the door is the whole input.
    location: 'pin',
    turnaround: 'Our analysts deliver in 3-5 working days',
    priceInfo: {
      price: '$1,200',
    },
  },
  // Charged through the sales-forecast fake door (pick an industry, then
  // Stripe) rather than /report/order, so it has no order row and never shows
  // up in My Reports - accepted for now, the analyst follows up by email.
  // The industry is asked for on the review step, because the Stripe product
  // is per-industry; the address is asked for like every other report's, and
  // rides along in the session metadata so the analyst starts with it.
  // Copy mirrors `app-feasibility-study-card`'s deliverables.
  {
    id: ReportTypes.FEASIBILITY_STUDY,
    sample: FEASIBILITY_STUDY_REPORT_SAMPLE,
    title: FEASIBILITY_STUDY_NAME,
    audiences: ['business'],
    audience: '(for business owners, lenders & investors)',
    category: 'Business / Lender',
    description:
      'Lender-ready study of one address, prepared by our analysts in two weeks',
    features: [
      'Location assessment: a metrics-based site ranking',
      'Market demand: estimated number of potential customers',
      '5-year sales projection across product categories',
      'Traffic analysis for all road directions',
      'Local customer profile: trends and purchasing power',
      'Competitive landscape around your address',
      'Direct analyst support',
    ],
    footer: 'PDF report',
    checkout: 'feasibility',
    // A study of one site. The trade area comes from the industry's own drive
    // time, not from a radius the buyer guesses at.
    location: 'pin',
    turnaround: 'Delivered in two weeks by our analysts',
    priceInfo: {
      price: FEASIBILITY_STUDY_PRICE_LABEL,
    },
  },
  {
    id: ReportTypes.COMPARATIVE_LOCATION,
    sample: COMPARATIVE_LOCATION_ANALYSIS_REPORT_SAMPLE,
    title: 'Comparative location analysis',
    audiences: ['business', 'realtor'],
    audience: '(for business)',
    category: 'Business',
    description: 'Compare 2 potential business locations in one report',
    features: ['Population', 'Income', 'Trends', 'Environment', 'Competition'],
    footer: 'PDF file',
    checkout: 'analyst',
    location: 'radius',
    turnaround: 'Our analysts reply within one business day',
    priceInfo: {
      price: '$499',
    },
  },
];
